import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { EQUIPMENT_LABEL, fmtMoney, fmtDate } from "../lib/format";
import { COMPANY_LOGO, COMPANY_NAME, PRODUCT_NAME } from "../lib/brand";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

const Field = ({ label, children, testid }) => (
  <div>
    <label className="label-base">{label}</label>
    {React.cloneElement(children, { "data-testid": testid })}
  </div>
);

export default function IntakeForm() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [mine, setMine] = useState([]);
  const [calc, setCalc] = useState({ busy: false, label: "", err: "" });
  const [f, setF] = useState({
    contact_name: user?.name || "", contact_email: user?.email || "", contact_phone: "",
    pickup_address: "", pickup_city: "", pickup_state: "TX", pickup_zip: "",
    dropoff_address: "", dropoff_city: "", dropoff_state: "CA", dropoff_zip: "",
    pickup_date: "", delivery_date: "",
    equipment_type: "van", weight: "", commodity: "", target_rate: "", notes: "",
  });

  const loadMine = () => {
    if (!user) return;
    api.get("/intake/mine").then(r => setMine(r.data)).catch(() => {});
  };
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user]);

  const estimate = async () => {
    setCalc({ busy: true, label: "", err: "" });
    try {
      const r = await api.get("/tools/distance", { params: { from_zip: f.pickup_zip, to_zip: f.dropoff_zip } });
      setCalc({ busy: false, label: `${r.data.road_miles} road mi · ${r.data.eta_label}`, err: "" });
    } catch (e) {
      setCalc({ busy: false, label: "", err: e.response?.data?.detail || "Could not calculate" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        ...f,
        weight: f.weight ? Number(f.weight) : null,
        target_rate: f.target_rate ? Number(f.target_rate) : null,
        pickup_date: f.pickup_date ? new Date(f.pickup_date).toISOString() : null,
        delivery_date: f.delivery_date ? new Date(f.delivery_date).toISOString() : null,
      };
      const r = await api.post("/intake", payload);
      setDone(r.data);
      loadMine();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to submit request");
    } finally { setBusy(false); }
  };

  const newRequest = () => {
    setDone(null); setCalc({ busy: false, label: "", err: "" });
    setF((p) => ({ ...p, pickup_address: "", pickup_city: "", pickup_zip: "", dropoff_address: "", dropoff_city: "", dropoff_zip: "", commodity: "", target_rate: "", notes: "", weight: "" }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="intake-home-link">
            <div className="w-9 h-9 rounded-md bg-white overflow-hidden flex items-center justify-center">
              <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-display font-bold text-sm">{COMPANY_NAME}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold">on {PRODUCT_NAME}</div>
            </div>
          </Link>
          <div className="flex-1" />
          {user
            ? <Link to="/app" className="text-white text-sm font-semibold hover:text-gold" data-testid="intake-app-link">Dashboard →</Link>
            : <Link to="/login" className="text-white text-sm font-semibold hover:text-gold" data-testid="intake-login-link">Sign in</Link>}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Load Intake</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-navy mb-2">Request a shipment</h1>
        <p className="text-slate-500 mb-8">Tell us what you need to move. Our brokerage team reviews every request and gets back to you with a rate.</p>

        {done ? (
          <div className="card-base p-8 text-center" data-testid="intake-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">✓</div>
            <h2 className="font-display text-2xl font-bold text-navy mb-2">Request received</h2>
            <p className="text-slate-500 mb-2">Reference <span className="font-mono font-semibold text-navy">{done.intake_id}</span></p>
            {done.distance_mi ? <p className="text-slate-500 mb-6">Estimated haul: <b>{done.distance_mi} mi</b> · {done.eta_label}</p> : <div className="mb-6" />}
            <button onClick={newRequest} className="btn-primary" data-testid="intake-new-btn">Submit another request</button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-base p-6 md:p-8 space-y-8" data-testid="intake-form">
            <section>
              <div className="font-display text-lg font-bold text-navy mb-4">Contact</div>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Your name" testid="intake-contact-name"><input className="input-base" value={f.contact_name} onChange={e => setF({...f, contact_name: e.target.value})} required /></Field>
                <Field label="Email" testid="intake-contact-email"><input type="email" className="input-base" value={f.contact_email} onChange={e => setF({...f, contact_email: e.target.value})} required /></Field>
                <Field label="Phone" testid="intake-contact-phone"><input className="input-base" value={f.contact_phone} onChange={e => setF({...f, contact_phone: e.target.value})} /></Field>
              </div>
            </section>

            <section>
              <div className="font-display text-lg font-bold text-navy mb-4">Pickup</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Address" testid="intake-pickup-address"><input className="input-base" value={f.pickup_address} onChange={e => setF({...f, pickup_address: e.target.value})} required /></Field>
                <Field label="City" testid="intake-pickup-city"><input className="input-base" value={f.pickup_city} onChange={e => setF({...f, pickup_city: e.target.value})} required /></Field>
                <Field label="State" testid="intake-pickup-state"><select className="input-base" value={f.pickup_state} onChange={e => setF({...f, pickup_state: e.target.value})}>{STATES.map(s => <option key={s}>{s}</option>)}</select></Field>
                <Field label="Zip" testid="intake-pickup-zip"><input className="input-base" value={f.pickup_zip} onChange={e => setF({...f, pickup_zip: e.target.value})} required /></Field>
                <Field label="Ready date" testid="intake-pickup-date"><input type="date" className="input-base" value={f.pickup_date} onChange={e => setF({...f, pickup_date: e.target.value})} /></Field>
              </div>
            </section>

            <section>
              <div className="font-display text-lg font-bold text-navy mb-4">Dropoff</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Address" testid="intake-dropoff-address"><input className="input-base" value={f.dropoff_address} onChange={e => setF({...f, dropoff_address: e.target.value})} required /></Field>
                <Field label="City" testid="intake-dropoff-city"><input className="input-base" value={f.dropoff_city} onChange={e => setF({...f, dropoff_city: e.target.value})} required /></Field>
                <Field label="State" testid="intake-dropoff-state"><select className="input-base" value={f.dropoff_state} onChange={e => setF({...f, dropoff_state: e.target.value})}>{STATES.map(s => <option key={s}>{s}</option>)}</select></Field>
                <Field label="Zip" testid="intake-dropoff-zip"><input className="input-base" value={f.dropoff_zip} onChange={e => setF({...f, dropoff_zip: e.target.value})} required /></Field>
                <Field label="Deliver by" testid="intake-delivery-date"><input type="date" className="input-base" value={f.delivery_date} onChange={e => setF({...f, delivery_date: e.target.value})} /></Field>
                <div className="flex flex-col justify-end">
                  <button type="button" onClick={estimate} disabled={calc.busy || !f.pickup_zip || !f.dropoff_zip} className="btn-outline w-full" data-testid="intake-estimate-btn">
                    {calc.busy ? "Calculating…" : "Estimate distance"}
                  </button>
                  {calc.label && <div className="text-xs text-navy font-semibold mt-1" data-testid="intake-estimate">{calc.label}</div>}
                  {calc.err && <div className="text-xs text-red-600 mt-1">{calc.err}</div>}
                </div>
              </div>
            </section>

            <section>
              <div className="font-display text-lg font-bold text-navy mb-4">Freight</div>
              <div className="grid md:grid-cols-4 gap-4">
                <Field label="Equipment" testid="intake-equipment"><select className="input-base" value={f.equipment_type} onChange={e => setF({...f, equipment_type: e.target.value})}>{Object.entries(EQUIPMENT_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
                <Field label="Weight (lbs)" testid="intake-weight"><input type="number" className="input-base" value={f.weight} onChange={e => setF({...f, weight: e.target.value})} /></Field>
                <Field label="Commodity" testid="intake-commodity"><input className="input-base" value={f.commodity} onChange={e => setF({...f, commodity: e.target.value})} /></Field>
                <Field label="Target rate (USD)" testid="intake-target-rate"><input type="number" className="input-base" value={f.target_rate} onChange={e => setF({...f, target_rate: e.target.value})} /></Field>
                <div className="md:col-span-4">
                  <label className="label-base">Notes</label>
                  <textarea className="input-base h-24" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} data-testid="intake-notes" />
                </div>
              </div>
            </section>

            {err && <div className="text-red-600 text-sm" data-testid="intake-error">{err}</div>}

            <div className="pt-4 border-t border-slate-200">
              <button type="submit" disabled={busy} className="btn-primary" data-testid="intake-submit">
                {busy ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        )}

        {user && mine.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-xl font-bold text-navy mb-4">Your recent requests</h2>
            <div className="card-base divide-y divide-slate-100" data-testid="intake-mine">
              {mine.map((m) => (
                <div key={m.intake_id} className="p-4 flex items-center gap-4" data-testid={`intake-row-${m.intake_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy text-sm">{m.pickup_city}, {m.pickup_state} → {m.dropoff_city}, {m.dropoff_state}</div>
                    <div className="text-xs text-slate-500">{fmtDate(m.created_at)} · {EQUIPMENT_LABEL[m.equipment_type]}{m.distance_mi ? ` · ${m.distance_mi} mi` : ""}{m.target_rate ? ` · target ${fmtMoney(m.target_rate)}` : ""}</div>
                    {m.status === "rejected" && m.rejection_reason && <div className="text-xs text-rose-600 mt-1">Reason: {m.rejection_reason}</div>}
                  </div>
                  {m.status === "approved" && m.load_id && <Link to={`/app/loads/${m.load_id}`} className="text-xs font-semibold text-gold hover:underline" data-testid={`intake-load-link-${m.intake_id}`}>View load →</Link>}
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${STATUS_STYLE[m.status] || "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
